import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { 
  Target, Plus, Users, BarChart3, FileText, Download, RefreshCw, Star
} from 'lucide-react';
import { toast } from 'sonner';
import { SendReportButton } from '../components/common/SendReportButton';
import { 
  StarRating, REVIEW_PERIODS, ReviewCard, TemplateCard,
  NewReviewDialog, EditReviewDialog, TemplateEditorDialog
} from '../components/kpi';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const KPIPage = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isViewer = user.role === 'viewer' || user.role === 'report_viewer';
  
  const [activeTab, setActiveTab] = useState('reviews');
  const [templates, setTemplates] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  
  // Filters
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  
  // Dialogs
  const [showNewReview, setShowNewReview] = useState(false);
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editingReview, setEditingReview] = useState(null);
  
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async () => {
    setLoading(true);
    try {
      const [templatesRes, reviewsRes, employeesRes] = await Promise.all([
        fetch(`${API_URL}/api/kpi/templates`),
        fetch(`${API_URL}/api/kpi/reviews?year=${selectedYear}`),
        fetch(`${API_URL}/api/employees`)
      ]);
      
      const templatesData = await templatesRes.json();
      const reviewsData = await reviewsRes.json();
      const employeesData = await employeesRes.json();
      
      setTemplates(templatesData);
      setReviews(reviewsData);
      setEmployees(employeesData.filter(e => e.is_active));
      
      const depts = [...new Set(employeesData.map(e => e.department).filter(Boolean))];
      setDepartments(depts);
    } catch (error) {
      toast.error('Failed to load KPI data');
    } finally {
      setLoading(false);
    }
  };
  
  const seedDefaultTemplates = async () => {
    try {
      const res = await fetch(`${API_URL}/api/kpi/templates/seed-defaults`, { method: 'POST' });
      const data = await res.json();
      toast.success(data.message);
      loadData();
    } catch (error) {
      toast.error('Failed to seed templates');
    }
  };
  
  const toggleEmployeeKPI = async (employee, enabled) => {
    try {
      let templateId = null;
      if (enabled) {
        const deptTemplate = templates.find(t => t.department?.toLowerCase() === employee.department?.toLowerCase());
        const posTemplate = templates.find(t => t.position?.toLowerCase() === employee.position?.toLowerCase());
        templateId = posTemplate?.id || deptTemplate?.id || templates[0]?.id;
      }
      
      const res = await fetch(`${API_URL}/api/employees/${employee.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kpi_settings: { kpi_enabled: enabled, template_id: templateId, custom_metrics: [] }
        })
      });
      
      if (res.ok) {
        toast.success(`KPI ${enabled ? 'enabled' : 'disabled'} for ${employee.name}`);
        loadData();
      } else {
        throw new Error('Failed to update');
      }
    } catch (error) {
      toast.error('Failed to update KPI settings');
    }
  };
  
  const filteredReviews = reviews.filter(r => {
    if (selectedEmployee && r.employee_id !== selectedEmployee) return false;
    if (selectedPeriod && r.review_period !== selectedPeriod) return false;
    return true;
  });
  
  const kpiEnabledEmployees = employees.filter(e => e.kpi_settings?.kpi_enabled);
  
  const handleExportSummary = async (format) => {
    setExporting(true);
    try {
      const periodParam = selectedPeriod && selectedPeriod !== 'all' ? `&period=${selectedPeriod}` : '';
      const response = await fetch(`${API_URL}/api/kpi/export/summary?format=${format}&year=${selectedYear}${periodParam}`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `KPI_Summary_${selectedYear}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success(`KPI Summary exported as ${format.toUpperCase()}`);
      } else {
        throw new Error('Export failed');
      }
    } catch (error) {
      toast.error('Failed to export KPI summary');
    } finally {
      setExporting(false);
    }
  };
  
  const handleExportReview = async (reviewId, format) => {
    try {
      const response = await fetch(`${API_URL}/api/kpi/export/review/${reviewId}?format=${format}`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `KPI_Review.${format === 'excel' ? 'xlsx' : 'pdf'}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success(`Review exported as ${format.toUpperCase()}`);
      }
    } catch (error) {
      toast.error('Failed to export review');
    }
  };

  const avgScore = reviews.length > 0 
    ? Math.round(reviews.reduce((sum, r) => sum + (r.overall_score || 0), 0) / reviews.length)
    : 0;
  
  const avgRating = reviews.length > 0 
    ? reviews.reduce((sum, r) => sum + (r.star_rating || 0), 0) / reviews.length
    : 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Target className="h-8 w-8 text-indigo-600" />
            KPI Management
          </h1>
          <p className="text-gray-500 mt-1">Track and manage employee performance indicators</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => handleExportSummary('pdf')} disabled={exporting}>
            <Download className="h-4 w-4 mr-2" /> PDF
          </Button>
          <Button variant="outline" onClick={() => handleExportSummary('excel')} disabled={exporting}>
            <Download className="h-4 w-4 mr-2" /> Excel
          </Button>
          <SendReportButton
            reportUrl={`/api/kpi/export/summary?format=pdf&year=${selectedYear}${selectedPeriod && selectedPeriod !== 'all' ? `&period=${selectedPeriod}` : ''}`}
            filename={`KPI_Summary_${selectedYear}.pdf`}
            defaultSubject={`KPI Summary ${selectedYear}`}
            variant="outline"
            size="default"
            className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
            testid="kpi-email"
          />
          <Button variant="outline" onClick={loadData}>
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
          {!isViewer && (
            <Button onClick={() => setShowNewReview(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="new-review-btn">
              <Plus className="h-4 w-4 mr-2" /> New Review
            </Button>
          )}
        </div>
      </div>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-indigo-600 font-medium">KPI Enabled</p>
                <p className="text-3xl font-bold text-indigo-700">{kpiEnabledEmployees.length}</p>
                <p className="text-xs text-gray-500">of {employees.length} employees</p>
              </div>
              <Users className="h-10 w-10 text-indigo-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600 font-medium">Reviews This Year</p>
                <p className="text-3xl font-bold text-green-700">{reviews.length}</p>
                <p className="text-xs text-gray-500">{reviews.filter(r => r.status === 'approved').length} approved</p>
              </div>
              <FileText className="h-10 w-10 text-green-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-600 font-medium">Avg Score</p>
                <p className="text-3xl font-bold text-amber-700">{avgScore}%</p>
                <StarRating rating={avgRating} size="sm" />
              </div>
              <Star className="h-10 w-10 text-amber-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 font-medium">Templates</p>
                <p className="text-3xl font-bold text-blue-700">{templates.length}</p>
                <p className="text-xs text-gray-500">active templates</p>
              </div>
              <BarChart3 className="h-10 w-10 text-blue-400" />
            </div>
          </CardContent>
        </Card>
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-gray-100">
          <TabsTrigger value="reviews" className="data-[state=active]:bg-white">
            <FileText className="h-4 w-4 mr-2" /> Reviews
          </TabsTrigger>
          <TabsTrigger value="employees" className="data-[state=active]:bg-white">
            <Users className="h-4 w-4 mr-2" /> Employee Settings
          </TabsTrigger>
          <TabsTrigger value="templates" className="data-[state=active]:bg-white">
            <Target className="h-4 w-4 mr-2" /> Templates
          </TabsTrigger>
        </TabsList>
        
        {/* Reviews Tab */}
        <TabsContent value="reviews" className="space-y-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex flex-wrap gap-4 items-end">
                <div className="flex-1 min-w-[200px]">
                  <Label>Employee</Label>
                  <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Employees" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Employees</SelectItem>
                      {kpiEnabledEmployees.map(emp => (
                        <SelectItem key={emp.id} value={emp.id}>
                          {emp.employee_code} - {emp.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-[150px]">
                  <Label>Period</Label>
                  <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Periods" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Periods</SelectItem>
                      {REVIEW_PERIODS.map(p => (
                        <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-[120px]">
                  <Label>Year</Label>
                  <Select value={selectedYear} onValueChange={setSelectedYear}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[2024, 2025, 2026].map(y => (
                        <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button variant="outline" onClick={() => { setSelectedEmployee(''); setSelectedPeriod(''); }}>
                  Clear Filters
                </Button>
              </div>
            </CardContent>
          </Card>
          
          {filteredReviews.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-700">No Reviews Found</h3>
                <p className="text-gray-500 mt-1">Create a new review to get started</p>
                {!isViewer && (
                  <Button className="mt-4" onClick={() => setShowNewReview(true)} data-testid="create-review-btn">
                    <Plus className="h-4 w-4 mr-2" /> Create Review
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {filteredReviews.map(review => (
                <ReviewCard 
                  key={review.id} 
                  review={review} 
                  onEdit={() => setEditingReview(review)}
                  onRefresh={loadData}
                  onExport={handleExportReview}
                  isViewer={isViewer}
                />
              ))}
            </div>
          )}
        </TabsContent>
        
        {/* Employee Settings Tab */}
        <TabsContent value="employees" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Employee KPI Activation</CardTitle>
              <CardDescription>Enable or disable KPI tracking for individual employees</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {employees.map(emp => (
                  <div key={emp.id} className="flex items-center justify-between p-3 rounded-lg border hover:bg-gray-50">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                        <span className="text-indigo-600 font-medium text-sm">
                          {emp.name.split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium">{emp.name}</p>
                        <p className="text-sm text-gray-500">{emp.employee_code} - {emp.position}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <Badge variant={emp.kpi_settings?.kpi_enabled ? "default" : "secondary"}>
                        {emp.department}
                      </Badge>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500">
                          {emp.kpi_settings?.kpi_enabled ? 'Enabled' : 'Disabled'}
                        </span>
                        <Switch
                          checked={emp.kpi_settings?.kpi_enabled || false}
                          onCheckedChange={(checked) => toggleEmployeeKPI(emp, checked)}
                          disabled={isViewer}
                          data-testid={`kpi-toggle-${emp.id}`}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold">KPI Templates</h3>
              <p className="text-sm text-gray-500">Manage department and position-specific KPI templates</p>
            </div>
            {!isViewer && (
              <div className="flex gap-2">
                {templates.length === 0 && (
                  <Button variant="outline" onClick={seedDefaultTemplates}>
                    <RefreshCw className="h-4 w-4 mr-2" /> Load Default Templates
                  </Button>
                )}
                <Button onClick={() => { setEditingTemplate(null); setShowTemplateEditor(true); }} data-testid="new-template-btn">
                  <Plus className="h-4 w-4 mr-2" /> New Template
                </Button>
              </div>
            )}
          </div>
          
          <div className="grid gap-4">
            {templates.map(template => (
              <TemplateCard 
                key={template.id} 
                template={template} 
                onEdit={() => { setEditingTemplate(template); setShowTemplateEditor(true); }}
                onRefresh={loadData}
                isViewer={isViewer}
              />
            ))}
          </div>
        </TabsContent>
      </Tabs>
      
      {/* Dialogs */}
      <NewReviewDialog 
        open={showNewReview} 
        onOpenChange={setShowNewReview}
        employees={employees}
        templates={templates}
        onSuccess={() => { loadData(); setShowNewReview(false); }}
      />
      
      {editingReview && (
        <EditReviewDialog
          open={!!editingReview}
          onOpenChange={() => setEditingReview(null)}
          review={editingReview}
          onSuccess={() => { loadData(); setEditingReview(null); }}
        />
      )}
      
      <TemplateEditorDialog
        open={showTemplateEditor}
        onOpenChange={setShowTemplateEditor}
        template={editingTemplate}
        departments={departments}
        onSuccess={() => { loadData(); setShowTemplateEditor(false); }}
      />
    </div>
  );
};

export default KPIPage;
